// src/modules/academy/pages/AcademyDashboard.tsx

import React from 'react';
import { useAppContext } from '../../../shared/context/AppContext';
import { useAcademyData } from '../hooks/useAcademyData';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import WelcomeSection from '../components/WelcomeSection';
import AcademyStats from '../components/AcademyStats';
import MyEvents from '../components/MyEvents';
import QuickActions from '../components/QuickActions';
import RecentActivity from '../components/RecentActivity';
import { UserRole } from '../types';
import { useNavigate } from 'react-router-dom';
import './AcademyDashboard.css';

const AcademyDashboard: React.FC = () => {
  const { state } = useAppContext();
  const { auth } = state;
  const navigate = useNavigate(); // AGREGAR esta línea

  const {
    academyUser,
    profile,
    isLoading,
    error,
    dataSource,
    refreshData,
    clearError
  } = useAcademyData();

  const getRoleIcon = (role: UserRole): string => {
    switch (role) {
      case 'participant': return '👨‍🎓';
      case 'speaker': return '🎤';
      case 'judge': return '⚖️';
      default: return '👤';
    }
  };

  const getRoleLabel = (role: UserRole): string => {
    switch (role) {
      case 'participant': return 'Participante';
      case 'speaker': return 'Ponente';
      case 'judge': return 'Juez';
      default: return 'Usuario';
    }
  };

  const getDataSourceInfo = () => {
    switch (dataSource) {
      case 'database':
        return {
          icon: '🟢',
          label: 'Datos de la Base de Datos',
          description: 'Información real obtenida de la base de datos'
        };
      case 'mixed':
        return {
          icon: '🟡',
          label: 'Datos Mixtos',
          description: 'Combinación de datos reales y simulados'
        };
      case 'simulated':
      default:
        return {
          icon: '🟠',
          label: 'Datos Simulados',
          description: 'Datos de ejemplo para demostración'
        };
    }
  };

  // Estados de carga
  if (isLoading) {
    return (
      <div className="academy-dashboard-loading">
        <div className="container container--md">
          <Card className="loading-card" padding="large">
            <Loading message="Cargando datos del usuario desde la base de datos..." />
            <p className="loading-details">
              Conectando con API • Verificando autenticación • Cargando perfil
            </p>
          </Card>
        </div>
      </div>
    );
  }

  // Error en la carga
  if (error && !academyUser) {
    return (
      <div className="academy-dashboard-error">
        <div className="container container--md">
          <Card className="error-card" padding="large">
            <ErrorMessage message={error} onClose={clearError} />
            <div className="error-actions">
              <Button variant="primary" onClick={refreshData}>
                Reintentar Carga
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Recargar Página
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Usuario no autenticado
  if (!auth.user || !academyUser) {
    return (
      <div className="academy-dashboard-unauthorized">
        <div className="container container--md">
          <Card className="unauthorized-card" padding="large">
            <h2>Acceso Denegado</h2>
            <p>Debes iniciar sesión para acceder al dashboard académico</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Iniciar Sesión
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const dataSourceInfo = getDataSourceInfo();

  return (
    <div className="academy-dashboard">
      <div className="container container--xl">
        {/* Indicador de fuente de datos */}
       

        {/* Error no crítico */}
        {error && academyUser && (
          <div className="non-critical-error">
            <ErrorMessage
              message={`Advertencia: ${error}`}
              onClose={clearError}
            />
          </div>
        )}

        {/* Sección de bienvenida */}
        <WelcomeSection user={academyUser} />

        {/* Estadísticas principales */}
        <section className="dashboard-section">
          <Card variant="glass" padding="large">
            <div className="competitions-quick-access">
              <div className="section-header">
                <h3 className="section-title">Competencias</h3>
                <p className="section-description">
                  Participa en competencias de belleza y muestra tu talento
                </p>
              </div>

              <div className="quick-actions-grid">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => navigate('/competitions')}
                >
                  Ver Competencias Activas
                </button>

                <button
                  className="btn btn-outline btn-lg"
                  onClick={() => navigate('/my-competitions')}
                >
                  Mis Competencias
                </button>
              </div>
            </div>
          </Card>
        </section>
{/*         
        <section className="dashboard-section">
          <AcademyStats
            userRoles={academyUser.roles}
          />
        </section> */}

        {/* Grid principal */}
        <div className="dashboard-grid">
          {/* Columna izquierda */}
          <div className="dashboard-left">
            {/* Mis eventos */}
            <section className="dashboard-section">
              <MyEvents
                userRoles={academyUser.roles}
                participations={profile?.participations}
              />
            </section>
          </div>

          {/* Columna derecha */}
          <div className="dashboard-right">
            {/* Acciones rápidas */}
            <section className="dashboard-section">
              <QuickActions userRoles={academyUser.roles} />
            </section>

            {/* Actividad reciente */}
            <section className="dashboard-section">
              <RecentActivity
                user={academyUser}
              />
            </section>
          </div>
        </div>

        {/* Información del perfil */}
        <section className="dashboard-section">
          <Card variant="glass" padding="large">
            <div className="profile-overview">
              <div className="profile-header">
                <h3 className="section-title">Mi Perfil Académico</h3>
                <div className="profile-actions">
                  <Button variant="outline" size="sm" disabled>
                    ✏️ Editar Perfil
                  </Button>
                </div>
              </div>

              <div className="profile-grid">
                <div className="profile-roles">
                  <h4>Mis Roles</h4>
                  <div className="roles-list">
                    {academyUser.roles.map((role, index) => (
                      <div key={index} className="role-item">
                        <span className="role-icon">{getRoleIcon(role)}</span>
                        <span className="role-label">{getRoleLabel(role)}</span>
                      </div>
                    ))}
                  </div>
                  <small className="roles-note">
                    {dataSource === 'database'
                      ? 'Roles determinados por tus participaciones reales en eventos'
                      : 'Roles de ejemplo - se actualizarán con datos reales'
                    }
                  </small>
                </div>

                <div className="profile-stats">
                  <h4>Estadísticas</h4>
                  {profile?.participations && profile.participations.length > 0 ? (
                    <div className="stats-real">
                      <div className="stat-real">
                        <span className="stat-label">Participaciones:</span>
                        <span className="stat-value">{profile.participations.length}</span>
                      </div>
                      <div className="stat-real">
                        <span className="stat-label">Certificados:</span>
                        <span className="stat-value">{profile.certificates?.length ?? 0}</span>
                      </div>
                      <div className="stat-real">
                        <span className="stat-label">Roles activos:</span>
                        <span className="stat-value">{profile.roles?.filter(r => r.is_active).length ?? 0}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="stats-placeholder">
                      <p>Las estadísticas aparecerán cuando tengas participaciones registradas</p>
                    </div>
                  )}
                </div>

                <div className="profile-details">
                  <h4>Información</h4>
                  <div className="details-list">
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{academyUser.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Miembro desde:</span>
                      <span className="detail-value">
                        {new Date(academyUser.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Último acceso:</span>
                      <span className="detail-value">
                        {academyUser.last_login
                          ? new Date(academyUser.last_login).toLocaleDateString()
                          : 'Primer acceso'
                        }
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Estado:</span>
                      <span className="detail-value">
                        {academyUser.is_active ? '✅ Activo' : '❌ Inactivo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* Debug panel - solo en desarrollo */}
        {process.env.NODE_ENV === 'development' && (
          <section className="dashboard-section">
            <Card variant="flat" padding="large">
              <div className="debug-panel">
                <h3>Panel de Debug</h3>
                <div className="debug-grid">
                  <div className="debug-item">
                    <strong>Fuente de datos:</strong> {dataSource}
                  </div>
                  <div className="debug-item">
                    <strong>Perfil cargado:</strong> {profile ? 'Sí' : 'No'}
                  </div>
                  <div className="debug-item">
                    <strong>Roles:</strong> {profile?.roles?.length || 0}
                  </div>
                  <div className="debug-item">
                    <strong>Participaciones:</strong> {profile?.participations?.length || 0}
                  </div>
                  <div className="debug-item">
                    <strong>Certificados:</strong> {profile?.certificates?.length || 0}
                  </div>
                </div>
                <details className="debug-details">
                  <summary>Ver datos completos del perfil</summary>
                  <pre className="debug-json">
                    {JSON.stringify(profile, null, 2)}
                  </pre>
                </details>
              </div>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
};

export default AcademyDashboard;